import {announce} from '@github-ui/aria-live'
import {useInfiniteQuery} from '@github-ui/react-query'
import {createServerPersistedQueryPersister} from '@github-ui/react-query-persister'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {IssueRelationshipsBlockedBy} from '@github-ui/server-persisted-queries'
import {useAnalytics} from '@github-ui/use-analytics'
import {LockIcon} from '@primer/octicons-react'
import {ActionList, Banner} from '@primer/react'
import {Suspense, useCallback, useMemo} from 'react'

import {LABELS} from '../../../constants/labels'
import {blockedByInfiniteQueryOptions} from './fetch-relationships'
import {
  RELATIONSHIPS_LOAD_MORE_ERROR,
  RelationshipsListAllViewPaginated,
  RelationshipsListItem,
  RelationshipsListViewLoadingSkeleton,
} from './PaginatedRelationshipsListAllView'

// RELAY-CLEANUP: This connection key is still used by the add/remove blocked-by mutations
// for optimistic cache updates. Remove when those mutations are migrated to TanStack.
export const BLOCKED_BY_LIST_ALL_RELAY_CONNECTION = 'LazyRelationshipsBlockedByListViewFragment__blockedBy'

export type LazyRelationshipsBlockedByListViewProps = {
  owner: string
  repo: string
  issueNumber: number
  blockedByCount: number
}
export function LazyRelationshipsBlockedByListView(props: LazyRelationshipsBlockedByListViewProps) {
  return (
    <Suspense fallback={<RelationshipsListViewLoadingSkeleton />}>
      <RelationshipsBlockedByListViewInternal {...props} />
    </Suspense>
  )
}

function RelationshipsBlockedByListViewInternal({
  owner,
  repo,
  issueNumber,
  blockedByCount,
}: LazyRelationshipsBlockedByListViewProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const persister = useMemo(
    () =>
      createServerPersistedQueryPersister({
        queryHash: IssueRelationshipsBlockedBy,
        sendAnalyticsEvent,
        maxAgeMs: DEFAULT_RELAY_CACHE_TTL,
        // Restored relationships may be stale within their TTL, so revalidate after restoring them.
        alwaysRefetch: true,
      }),
    [sendAnalyticsEvent],
  )
  const {data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError, isPending} =
    useInfiniteQuery(blockedByInfiniteQueryOptions(owner, repo, issueNumber, persister))

  const onLoadMore = useCallback(async () => {
    const result = await fetchNextPage()
    if (result.isError) {
      announce(RELATIONSHIPS_LOAD_MORE_ERROR, {assertive: true})
    }
  }, [fetchNextPage])

  const nodes = useMemo(() => data?.pages.flatMap(page => page.nodes) ?? [], [data])
  const repositoryNameWithOwner = data?.pages[0]?.repositoryNameWithOwner ?? ''

  if (isPending) return <RelationshipsListViewLoadingSkeleton />
  // `isFetchNextPageError` covers a failed "load more", which the list surfaces inline. A failed
  // background revalidation is a different failure, and it keeps the restored pages — so only throw
  // when there is nothing usable to show.
  if (error && !isFetchNextPageError && !data) throw error

  if (!nodes.length) {
    return (
      <Banner variant="info">
        <Banner.Title>{LABELS.emptyBlockedByList}</Banner.Title>
      </Banner>
    )
  }

  // There is a PAGE_SIZE pagination limit for edges. If we have fewer visible edges than the total count,
  // and we've reached the last page (no next page available), then we can conclude that some issues
  // are inaccessible due to privacy restrictions.
  const isLastPage = !hasNextPage
  const hasInaccessibleBlockedByIssues = nodes.length < blockedByCount && isLastPage

  return (
    <RelationshipsListAllViewPaginated
      accessibleTitle={LABELS.relationNames.blockedByIssues}
      isLoadingNext={isFetchingNextPage}
      hasLoadMoreError={isFetchNextPageError}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
    >
      {nodes.map(node => (
        <RelationshipsListItem
          key={node.id}
          issue={node}
          sourceIssueRepositoryNameWithOwner={repositoryNameWithOwner}
        />
      ))}
      {hasInaccessibleBlockedByIssues && (
        <ActionList.Item disabled aria-disabled="true" className="tmp-pl-3">
          <ActionList.LeadingVisual>
            <LockIcon size={16} />
          </ActionList.LeadingVisual>
          <span className="text-small color-fg-muted" data-testid="private-blocked-by-notice-modal">
            Private issues are hidden.
          </span>
        </ActionList.Item>
      )}
    </RelationshipsListAllViewPaginated>
  )
}
